import React, { useEffect, useRef, useState, useMemo } from 'react';
import './BackgroundTest.css';
import '../Hero/AnimatedTitle.css';
import '../Hero/StaticTitle.css';
import AnimatedTitle from '../Hero/AnimatedTitle';
import StaticTitle from '../Hero/StaticTitle';
import DemoPreview from '../Hero/DemoPreview';

const BackgroundTest: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const subtitleRef = useRef<HTMLDivElement>(null);
  const [isDark, setIsDark] = useState(false);
  const [showSecondLine, setShowSecondLine] = useState(false);
  const [showThirdLine, setShowThirdLine] = useState(false);

  // Mover swapWords para fora do JSX para evitar recriação
  const swapWords = useMemo(() => ({
    phrases: [
      "Mais clientes",
      "Menos esforço",
      "Mais apólices",
      "Menos papelada",
      "Mais vendas",
      "Menos fricção",
      "Mais leads",
      "Menos trabalho",
      "Mais produtividade",
      "Menos stress",
      "Mais valor",
      "Menos ruído",
      "Mais negócios",
      "Menos complicação",
      "Formato positivo/directo",
      "Cresça sem esforço",
      "Lucro sem stress",
      "Produtividade imediata",
      "Vendas aceleradas",
      "Sucesso simplificado",
      "Automação inteligente",
      "Eficiência máxima",
      "Resultados garantidos",
      "Performance superior",
      "Clientes fidelizados"
    ]
  }), []);

  // Usar o tema baseado no sistema de temas global
  useEffect(() => {
    const checkTheme = () => {
      // Verificar se está em dark mode através do data-theme ou das variáveis CSS
      const dataTheme = document.documentElement.getAttribute('data-theme');
      const bgColor = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();

      // Se data-theme está definido, usar isso
      if (dataTheme) {
        setIsDark(dataTheme === 'dark');
      } else {
        // Senão, verificar pela cor de fundo
        setIsDark(bgColor === '#0b0b10');
      }
    };

    // Verificar tema inicial
    checkTheme();

    // Escutar mudanças no data-theme
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => {
      observer.disconnect();
    };
  }, []);

  // Mostrar a segunda linha depois que a primeira terminar
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSecondLine(true);
    }, 1000); // Aparecer logo depois do título-1, antes do CTA

    return () => clearTimeout(timer);
  }, []);

  // Mostrar terceiro título após o segundo
  useEffect(() => {
    if (showSecondLine) {
      const timer = setTimeout(() => {
        setShowThirdLine(true);
      }, 1000); // Aparecer 1 segundo depois do segundo título

      return () => clearTimeout(timer);
    }
  }, [showSecondLine]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // --- Fix para unidades vh (mobile) ---
    function setVh() {
      document.documentElement.style.setProperty('--vh', (window.innerHeight * 0.01) + 'px');
    }
    setVh();
    window.addEventListener('resize', setVh);

    // Função para detectar tema (usa o estado)
    function isDarkMode() {
      return isDark;
    }

    // Função para limpar canvas quando tema muda
    function clearCanvas() {
      if (!ctx) return;
      ctx.clearRect(0, 0, state.w, state.h);
    }

    // Parâmetros por URL (ex.: ?density=high&amp=1.1&speed=1.0&rot=1.0)
    const params = new URLSearchParams(window.location.search);
    const DENSITY = { low: 12, med: 16, high: 22, max: 28 }[(params.get('density') || 'high').toLowerCase()] || 22; // colunas por 1000px
    const AMP = parseFloat(params.get('amp') || '1.0');     // amplitude
    const SPEED = parseFloat(params.get('speed') || '1.0'); // velocidade temporal
    const ROTFAC = parseFloat(params.get('rot') || '1.0');  // sensibilidade rotação

    // Canvas + contexto
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const state = { w: 0, h: 0, dpr: Math.min(window.devicePixelRatio || 1, 2), time: 0 };

    // Câmara simples
    const cam = { x: 0, y: 8, z: 22, yaw: 0, pitch: -0.35, fov: 700, zoom: 1 };

    // Malha 3D (x,z) -> y = wave; guardamos pontos base
    const grid = { cols: 0, rows: 0, gap: 28, points: [] as any[] };

    // Interação
    const mouse = { x: 0, y: 0, down: false, lastX: 0, lastY: 0 };

    function resize() {
      if (!canvas || !ctx) return;
      state.w = canvas.clientWidth;
      state.h = canvas.clientHeight;
      canvas.width = Math.floor(state.w * state.dpr);
      canvas.height = Math.floor(state.h * state.dpr);
      ctx.setTransform(state.dpr, 0, 0, state.dpr, 0, 0);

      grid.gap = Math.max(14, Math.floor(1000 / DENSITY));
      grid.cols = Math.floor(state.w / grid.gap) + 6; // extra margem
      grid.rows = Math.floor(state.h / grid.gap) + 6;

      // construir grelha centrada
      grid.points = new Array(grid.cols * grid.rows);
      const halfW = grid.cols * grid.gap * 0.5;
      const halfH = grid.rows * grid.gap * 0.5;
      let i = 0;
      for (let r = 0; r < grid.rows; r++) {
        for (let c = 0; c < grid.cols; c++) {
          const x = c * grid.gap - halfW;
          const z = r * grid.gap - halfH;
          grid.points[i++] = { x: x, z: z, y: 0 };
        }
      }
    }

    // Função de onda (parecida com o teu exemplo, mas própria)
    function wave(x: number, z: number, t: number) {
      const kx = 0.015, kz = 0.019;
      return Math.sin(x * kx + t * 1.6) * 10 * AMP + Math.cos(z * kz + t * 1.1) * 6 * AMP;
    }

    // Matriz de rotação yaw/pitch
    function rotate3D(pt: any, yaw: number, pitch: number) {
      const cy = Math.cos(yaw), sy = Math.sin(yaw);
      const cp = Math.cos(pitch), sp = Math.sin(pitch);
      // yaw (y)
      const x1 = pt.x * cy - pt.z * sy;
      const z1 = pt.x * sy + pt.z * cy;
      // pitch (x)
      const y2 = pt.y * cp - z1 * sp;
      const z2 = pt.y * sp + z1 * cp;
      return { x: x1, y: y2, z: z2 };
    }

    // Projeção em 2D
    function project(pt: any) {
      const p = rotate3D(pt, cam.yaw, cam.pitch);
      // translação da câmara (cam.x/y/z)
      const zx = p.z - cam.z; // camera olha para -z implícito
      const scale = cam.fov / (cam.fov - zx) * cam.zoom;
      return {
        x: state.w * 0.5 + (p.x - cam.x) * scale,
        y: state.h * 0.5 + (p.y - cam.y) * scale,
        s: Math.max(0, scale)
      };
    }

    // Loop de simulação/desenho
    let lastTs = 0, rafId = 0;
    function frame(ts: number) {
      if (!lastTs) lastTs = ts;
      lastTs = ts;
      state.time += (0.016 * SPEED);

      if (!ctx) return;

      // Limpar completamente o canvas
      ctx.clearRect(0, 0, state.w, state.h);

      // fundo véu (reduzir para não desbotar o background)
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = isDarkMode() ? '#0b0b10' : 'rgba(255,255,255,0)';
      ctx.fillRect(0, 0, state.w, state.h);

      // linhas + pontos
      ctx.globalCompositeOperation = isDarkMode() ? 'lighter' : 'source-over';
      ctx.lineWidth = isDarkMode() ? 0.8 : 1;

      // Atualizar e desenhar linhas por filas
      let i = 0;
      for (let r = 0; r < grid.rows; r++) {
        // pré-calcular projeções para a fila
        const projRow = new Array(grid.cols);
        for (let c = 0; c < grid.cols; c++) {
          const pt = grid.points[i++];
          pt.y = wave(pt.x, pt.z, state.time);
          const pr = project(pt);
          projRow[c] = pr;
        }
        // linhas horizontais
        if (ctx) {
          ctx.beginPath();
          for (let c2 = 0; c2 < grid.cols; c2++) {
            const p2 = projRow[c2];
            if (c2 === 0) ctx.moveTo(p2.x, p2.y);
            else ctx.lineTo(p2.x, p2.y);
          }
          // Ajustar cores/contraste baseado no tema - aumentar visibilidade
          const lineAlpha = isDarkMode() ? 0.15 : 0.25;
          const pointAlpha = isDarkMode() ? 0.4 : 0.5;

          // Remover glow para evitar rasto
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;

          // Usar roxo da marca para as linhas
          const lineColor = '108,70,255'; // #6C46FF
          ctx.strokeStyle = `rgba(${lineColor},${lineAlpha})`;
          ctx.stroke();
          // pontos - também em roxo da marca (menores)
          for (let c3 = 0; c3 < grid.cols; c3++) {
            const p3 = projRow[c3];
            const rsize = Math.min(2.5, 0.8 + p3.s * 1.2);
            const dotColor = '108,70,255'; // #6C46FF
            ctx.fillStyle = `rgba(${dotColor},${Math.min(0.8, 0.3 + p3.s * pointAlpha)})`;
            ctx.beginPath();
            ctx.arc(p3.x, p3.y, rsize, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      rafId = requestAnimationFrame(frame);
    }

    // Interação (zoom removido, fixo no valor inicial)

    function onPointerDown(e: PointerEvent) {
      mouse.down = true;
      mouse.lastX = e.clientX;
      mouse.lastY = e.clientY;
    }

    function onPointerUp() {
      mouse.down = false;
    }

    function onPointerMove(e: PointerEvent) {
      if (!mouse.down) {
        // rotação suave seguindo o cursor apenas na horizontal
        const nx = (e.clientX / state.w) - 0.5;
        cam.yaw = nx * 0.4 * ROTFAC;
        // Manter pitch fixo no hover
        cam.pitch = -0.35;
      } else {
        const dx = (e.clientX - mouse.lastX) / state.w;
        const dy = (e.clientY - mouse.lastY) / state.h;
        cam.yaw += dx * 1.3 * ROTFAC;
        // Lock Y at highest available value (minimum pitch)
        cam.pitch = Math.max(-1.2, Math.min(0.3, cam.pitch));
        mouse.lastX = e.clientX;
        mouse.lastY = e.clientY;
      }
    }

    // Boot + testes
    function boot() {
      resize();
      window.addEventListener('resize', resize);
      window.addEventListener('pointerdown', onPointerDown, { passive: true });
      window.addEventListener('pointerup', onPointerUp, { passive: true });
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      rafId = requestAnimationFrame(frame);

      // SELF-TESTS (não alterar a não ser que estejam errados)
      const tests: any[] = [];
      function addTest(name: string, pass: boolean) {
        tests.push({ name: name, pass: !!pass });
      }
      addTest('Canvas presente', !!canvas);
      addTest('Contexto 2D disponível', !!ctx);
      addTest('Grid criada', grid && grid.points.length > 0 && grid.cols > 0 && grid.rows > 0);
      addTest('Projecção 2D funciona', (function () {
        const p = project({ x: 0, y: 0, z: 0 });
        return isFinite(p.x) && isFinite(p.y) && p.s >= 0;
      })());
      addTest('RAF agendado', typeof rafId === 'number');

      const debug = (new URLSearchParams(window.location.search)).get('debug') === '1';
      const badge = document.getElementById('test-badge');
      if (badge && debug) {
        const ok = tests.every(function (t) {
          return t.pass;
        });
        badge.style.display = 'block';
        badge.textContent = ok ? 'Self‑tests: PASS ✅' : 'Self‑tests: FAIL ❌ (ver consola)';
      }
    }

    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      boot();
    } else {
      document.addEventListener('DOMContentLoaded', boot);
    }

    // Cleanup
    window.addEventListener('beforeunload', function () {
      cancelAnimationFrame(rafId);
    });

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('resize', setVh);
      window.removeEventListener('resize', resize);
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointermove', onPointerMove);
    };
  }, [isDark]);

  // Debug effect to log background changes
  useEffect(() => {


    // Forçar limpeza do canvas quando tema muda
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);

      }
    }
  }, [isDark]);

  return (
    <section id="home" className="hero" ref={sectionRef} style={{ background: 'transparent', position: 'relative' }}>
      {/* BACKGROUND: Particle Wave 3D, Vanilla Canvas (sem libs) */}
      <div className="bg-layer" aria-hidden="true" style={{ background: 'transparent' }}>
        <div id="bg-base" style={{
          position: 'absolute',
          inset: 0,
          background: 'var(--bg)', // Usar variável CSS do sistema de temas
          zIndex: 0
        }}></div>
        <canvas
          id="bg-canvas"
          ref={canvasRef}
          style={{
            position: 'absolute',
            top: '10%',
            left: 0,
            right: 0,
            width: '100%',
            height: '70%',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        ></canvas>
        <div style={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 2
        }}></div>
      </div>

      {/* CONTEÚDO - Título e texto */}
      <div className="content main-title-content">
        <div className="hero-content">
          {/* Container do título completo - reserva espaço para ambas as linhas */}
          <div className="title-container">
            {/* Primeira linha do título */}
            <AnimatedTitle
              baseText="Multiplique a Sua Produtividade"
              className="title-line-1"
              maxLines={1}
              typewriter={true}
            />

            {/* Segunda linha do título - sempre renderizada */}
            <div className="title-line-2-container">
              <AnimatedTitle
                baseText="Mais Valor"
                swapWords={swapWords}
                className={`title-line-2 ${showSecondLine ? 'show' : ''}`}
                maxLines={1}
              />
            </div>
          </div>

          {/* Terceiro título */}
          <div className={`title-line-3-container ${showThirdLine ? 'show' : ''}`}>
            <h3 className="title-line-3">
              O futuro da mediação de seguros começa aqui: um colega de trabalho que assume as tarefas operacionais para que os mediadores possam dedicar-se ao contacto humano.
            </h3>
          </div>

          <div className="cta">
            <a className="btn ripple" href="#comecar">
              <svg className="cta-logo" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                <path fill="currentColor" fillRule="evenodd" d="M9.504 1.132a1 1 0 01.992 0l1.75 1a1 1 0 11-.992 1.736L10 3.152l-1.254.716a1 1 0 11-.992-1.736l1.75-1zM5.618 4.504a1 1 0 01-.372 1.364L5.016 6l.23.132a1 1 0 11-.992 1.736L4 7.723V8a1 1 0 01-2 0V6a.996.996 0 01.52-.878l1.734-.99a1 1 0 011.364.372zm8.764 0a1 1 0 011.364-.372l1.733.99A1.002 1.002 0 0118 6v2a1 1 0 11-2 0v-.277l-.254.145a1 1 0 11-.992-1.736l.23-.132-.23-.132a1 1 0 01-.372-1.364zm-7 4a1 1 0 011.364-.372L10 8.848l1.254-.716a1 1 0 11.992 1.736L11 10.58V12a1 1 0 11-2 0v-1.42l-1.246-.712a1 1 0 01-.372-1.364zM3 11a1 1 0 011 1v1.42l1.246.712a1 1 0 11-.992 1.736l-1.75-1A1 1 0 012 14v-2a1 1 0 011-1zm14 0a1 1 0 011 1v2a1 1 0 01-.504.868l-1.75 1a1 1 0 11-.992-1.736L16 13.42V12a1 1 0 011-1zm-9.618 5.504a1 1 0 011.364-.372l.254.145V16a1 1 0 112 0v.277l.254-.145a1 1 0 11.992 1.736l-1.735.992a.995.995 0 01-1.022 0l-1.735-.992a1 1 0 01-.372-1.364z" clipRule="evenodd"></path>
              </svg>
              Fale connosco
            </a>
          </div>

          {/* Demo Preview Component - agora antes do subtítulo */}
          <DemoPreview />

          {/* Subtítulo unificado */}
          <div className="subtitle-container">
            <StaticTitle
              baseText="Otimize o trabalhado repetitivo e administrativo, foque a sua equipa em fazer crescer a carteira.<br><span style='color: var(--brand);'>Poupe 40h/mês em simulações, análises de clientes e emails.</span>"
              className="subtitle-static"
              headingLevel="h4"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default BackgroundTest;
